<script lang="ts" setup>
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Dropdown from 'primevue/dropdown'
import { computed, onMounted, ref } from 'vue'
import type { Product, YarnUsed } from "@/types/product-types"
import { generateUniqueId } from "@/constants"
import router from "@/router"
import {addYarnUsedToProduct, editYarnUsedForProduct, deleteYarnUsedFromProduct, getProductById} from "@/api/products"
import { useProductSpecificationForm } from "@/composables/useProductSpecificationForm"
import { yarnType } from "@/constants/yarns"
import { Icon } from "@iconify/vue"
import Button from "primevue/button"


const props = defineProps<{
  yarnUsed?: YarnUsed[]
}>()

const emit = defineEmits(['update:yarnUsed'])

const { loading, showToast } = useProductSpecificationForm('view')
const editingRow = ref(null)
const searchQuery = ref('')
const yarnsUsed = ref<YarnUsed[]>(props.yarnUsed || [])
const productId = router.currentRoute.value.params.id as string
const product = ref<Product>()
const isAddingNew = ref<boolean>(false)


const isEditingRow = (rowId: string) => editingRow.value === rowId

const addYarnUsed = () => {
  const id = generateUniqueId()

  const newRow = {
    id,
    productSpecificationId: productId,
    yarnCount: '',
    yarnColour: '',
    yarnType: 0,
    percentageComposition: 0,
    feeders: 0,
    isNew: true,
  }

  yarnsUsed.value.push({...newRow})
  handleEnableEdit(id)
  isAddingNew.value = true
}

const handleEnableEdit = (id: any) => {
  editingRow.value = id
}

const handleSave = async (index: number, id: string) => {
  const item = yarnsUsed.value.find((item) => item.id === id)
  if (!item) {
    return
  }

  editingRow.value = null
  isAddingNew.value = false

  const yarnUsed: YarnUsed = {
    productSpecificationId: productId,
    yarnCount: item.yarnCount,
    yarnColour: item.yarnColour,
    yarnType: item.yarnType,
    percentageComposition: item.percentageComposition,
    feeders: item.feeders,
  }

  loading.value = true
  try {
    await addYarnUsedToProduct(productId, yarnUsed)
    showToast('success', 'Yarn used added successfully')
    const updatedProduct = await getProductById(productId)
    yarnsUsed.value = updatedProduct.yarnsUsed || []
  } catch (error) {
    showToast('error', 'Failed to add yarn used')
  } finally {
    loading.value = false
  }
}

const handleDelete = (yarnUsedId: string) => {
  deleteYarnUsed(yarnUsedId)
}

const deleteYarnUsed = async (yarnUsedId: string) => {
  loading.value = true
  try {
    await deleteYarnUsedFromProduct(productId, yarnUsedId)
    showToast('success', 'Yarn deleted successfully')
    const updatedProduct = await getProductById(productId)
    yarnsUsed.value = updatedProduct.yarnsUsed || []
  } catch (error) {
    showToast('error', 'Failed to delete yarn')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  loading.value = true
  product.value = await getProductById(productId)
  yarnsUsed.value = product?.value?.yarnsUsed.map((item) => ({
    ...item,
  })) || []
  loading.value = false
})

const filteredYarnUsed = computed(() => {
  const query = searchQuery.value.toLowerCase()
  return yarnsUsed.value.filter(
    (yarn: YarnUsed) =>
      yarn.yarnColour?.toLowerCase().includes(query) ||
      yarn.percentageComposition?.toString().includes(query) ||
      yarn.feeders?.toString().includes(query)
  )
})

const handleUpdate = async (index: number, id: string) => {
  const item = yarnsUsed.value.find((item) => item.id === id)
  if (!item) {
    return
  }
  editingRow.value = null

  const yarnUsed: YarnUsed = {
    id: item.id,
    productSpecificationId: productId,
    yarnCount: item.yarnCount,
    yarnColour: item.yarnColour,
    yarnType: item.yarnType,
    percentageComposition: item.percentageComposition,
    feeders: item.feeders,
  }

  loading.value = true
  try {
    await editYarnUsedForProduct(productId, yarnUsed, yarnUsed.id)
    showToast('success', 'Yarn updated successfully')
    const updatedProduct = await getProductById(productId)
    yarnsUsed.value = updatedProduct.yarnsUsed || []
  } catch (error) {
    console.error('Failed to update yarn', error)
    showToast('error', 'Failed to update yarn')
  } finally {
    loading.value = false
  }
}

</script>

<template>
  <DataTable
    :value="filteredYarnUsed"
    paginator
    :rows="10"
    class="overflow-hidden"
    stripedRows
    paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
    :rowsPerPageOptions="[10, 25]"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} yarns used"
    :loading="loading"
    :pt="{
      table: loading && 'min-h-[30rem]'
    }"
    :pt-options="{
      mergeProps: true
    }"
    style="width: 100%"
  >
    <Column
      field="yarnCount"
      header="Yarn Count"
      sortable
      style="width: 15%"
    >
      <template #body="slotProps">
        <InputText
          v-model="slotProps.data.yarnCount"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          :disabled="!isEditingRow(slotProps.data.id)"
          class="w-full"
          @blur="slotProps.data.yarnCount = slotProps.data.yarnCount?.trim()"
        />
      </template>
    </Column>

    <Column
      field="yarnColour"
      header="Yarn Colour"
      sortable
      style="width: 20%"
    >
      <template #body="slotProps">
        <InputText
          v-model="slotProps.data.yarnColour"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          :disabled="!isEditingRow(slotProps.data.id)"
          class="w-full"
          @blur="slotProps.data.yarnColour = slotProps.data.yarnColour?.trim()"
        />
      </template>
    </Column>

    <Column
      field="yarnType"
      header="Yarn Type"
      style="width: 20%"
      sortable
    >
      <template #body="slotProps">
        <Dropdown
          v-model="slotProps.data.yarnType"
          :options="yarnType"
          optionLabel="name"
          optionValue="id"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          :disabled="!isEditingRow(slotProps.data.id)"
          class="w-full"
        />
      </template>
    </Column>

    <Column
      field="percentageComposition"
      header="% Comp"
      style="width: 12%"
      sortable
    >
      <template #body="slotProps">
        <InputNumber
          v-model="slotProps.data.percentageComposition"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          :disabled="!isEditingRow(slotProps.data.id)"
          suffix="%"
          :min="0"
          :max="100"
          class="w-full"
        />
      </template>
    </Column>

    <Column
      field="feeders"
      header="Feeders"
      style="width: 12%"
      sortable
    >
      <template #body="slotProps">
        <InputNumber
          v-model="slotProps.data.feeders"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          :disabled="!isEditingRow(slotProps.data.id)"
          class="w-full"
        />
      </template>
    </Column>

    <Column field="" header="Actions" style="width: 21%">
      <template #body="slotProps">
        <div v-if="!isEditingRow(slotProps.data.id) && !slotProps.data.isNew" class="flex gap-1">
          <Button
            v-ripple
            outlined
            size="small"
            class="flex-1 gap-x-2"
            @click="handleEnableEdit(slotProps.data.id)"
            :loading="loading"
            :disabled="false"
          >
            <span class="text-xs">Edit</span>
            <Icon icon="basil:edit-outline" class="text-lg"/>
          </Button>
          <Button
            v-ripple
            outlined
            severity="danger"
            size="small"
            class="flex-1 gap-x-2"
            @click="handleDelete(slotProps.data.id)"
            :loading="loading"
            :disabled="false"
          >
            <span class="text-xs">Delete</span>
            <Icon icon="basil:trash-outline" class="text-lg"/>
          </Button>
        </div>
        <div v-else class="flex gap-1">
          <Button
            v-ripple
            outlined
            size="small"
            class="flex-1 gap-x-2"
            @click="handleSave(slotProps.index, slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            v-if="slotProps.data.isNew"
          >
            <span class="text-xs">Save</span>
            <Icon icon="basil:check-outline" class="text-xl"/>
          </Button>
          <Button
            v-ripple
            outlined
            size="small"
            class="flex-1"
            @click="handleUpdate(slotProps.index, slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            v-else
          >
            <span class="text-xs">Update</span>
          </Button>
        </div>
      </template>
    </Column>
    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl"/>
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl"/>
      </Button>
    </template>
  </DataTable>

  <div class="px-4 py-2">
    <Button
      v-ripple
      outlined
      class="gap-x-2 w-full sm:w-auto"
      @click="addYarnUsed()"
      :loading="loading"
      :disabled="isAddingNew"
    >
      <span>Add New Yarn</span>
      <Icon icon="basil:plus-solid" class="text-2xl"/>
    </Button>
  </div>
</template>

<style scoped>
:deep(.p-datatable-table) {
  table-layout: fixed !important;
  width: 100% !important;
}

:deep(.p-inputtext),
:deep(.p-dropdown),
:deep(.p-inputnumber) {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

:deep(.p-dropdown-panel) {
  max-width: 100% !important;
}

:deep(.p-button) {
  min-width: auto !important;
  padding: 0.5rem !important;
}

.p-datatable {
  width: 100% !important;
  overflow-x: hidden !important;
}

.p-datatable .p-datatable-wrapper {
  overflow-x: hidden !important;
}

:deep(.p-datatable-tbody > tr > td) {
  padding: 0.5rem !important;
  vertical-align: middle !important;
}

:deep(.p-datatable-thead > tr > th) {
  padding: 0.5rem !important;
  vertical-align: middle !important;
}
</style>